import React, { useState, useMemo } from 'react';
import {
    Upload, Button, Steps, Table, Tag, Alert, Progress,
    Row, Col, Tooltip, Result,
    Space, Typography, Select,
    Divider,
} from 'antd';
import {
    InboxOutlined, CheckCircleOutlined, WarningOutlined,
    FileExcelOutlined, CloudUploadOutlined, BarChartOutlined,
    DownloadOutlined, ReloadOutlined, ExclamationCircleOutlined,
    CloseCircleOutlined,
} from '@ant-design/icons';
import { XLSX } from '../../utils/xlsxShim';
import { exporterRapportEncaissements, categoriserErreur, RAISONS, ACTIONS, TAG_COLORS, couleurAction } from '../../utils/rapportUtils';
import Layout from '../../components/Layout';
import axiosInstance from '../../variables/api/axiosInstance';
import { toast } from 'react-toastify';
import { Trans, useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { localeIntl } from '../../i18n/format';
import PageHeader from '../../components/PageHeader';

const { Dragger } = Upload;
const { Title, Text } = Typography;

const ANNEES = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i);

// ── Helpers ────────────────────────────────────────────────────────
const fMt = (v) => Number(v ?? 0).toLocaleString(localeIntl(), { maximumFractionDigits: 0 });
// Avertissements de lecture : produits au moment du dépôt du fichier, dans la langue de l'interface.
const avert = (cle, options) => i18n.t(`reprise:avertissements.${cle}`, options);
const clean = (v) => (v != null ? String(v).trim().replace(/\u00a0/g, '') : null);

const dateFromRaw = (raw) => {
    if (!raw) return null;
    if (raw instanceof Date) return raw.toISOString().split('T')[0];
    if (typeof raw === 'number') {
        const d = XLSX.SSF.parse_date_code(raw);
        if (d && d.y) return `${d.y}-${String(d.m).padStart(2, '0')}-${String(d.d).padStart(2, '0')}`;
    }
    if (typeof raw === 'string') return raw.split('T')[0];
    return null;
};

// ── Parser Excel - Universel (basé sur les en-têtes) ──────────────
//
// La structure des colonnes VARIE selon l'année (col "ANNEE SURV" en plus en
// 2023, etc.), ce qui décale toutes les positions. On ne se base donc JAMAIS
// sur un index fixe : chaque colonne est repérée par le libellé de l'en-tête
// (ligne 1), de façon insensible à la casse, aux accents et aux espaces.

const SYNONYMES_ENC = {
    sinManuel: ['NBNCBTG', 'NBNCBTOGO'],
    sinHomol: ['NBNCBHOMOLOGUE', 'NHOMOLOGUE'],
    dateReception: ['DATERECEPTION', 'DATEREC'],
    dateSinistre: ['DATESINISTRE', 'DATESIN'],
    payeur: ['PAYEUR'],
    assure: ['ASSURES', 'ASSURE'],
    modePaiem: ['MODEDEPAIEMENT', 'MODEPAIEMENT'],
    banque: ['BANQUE'],
    cheque: ['NCHEQUEOUVIREMENT', 'NCHEQUE', 'CHEQUEOUVIREMENT', 'VIREMENT'],
    lieu: ['LIEUDESURVENANCE', 'LIEUSURVENANCE', 'LIEU'],
    total: ['TOTALENCAISSE', 'MONTANTTOTALENCAISSE', 'MONTANTTOTAL'],
    principal: ['PRINCIPALENCAISSE', 'PRINCIPAL'],
    fg: ['FGENCAISSE', 'FG'],
    pgReel: ['PGREELLEMENTENCAISSE', 'PGREEL', 'PGREELLEMENT'],
    pgTheo: ['PGTHEORIQUE', 'PGTHEO'],
};

function normaliserHeader(v) {
    return String(v || '')
        .toUpperCase()
        .normalize('NFD')               // décompose accents (é → e + diacritique)
        .replace(/[^A-Z0-9]/g, '');     // ne garde que lettres/chiffres (retire diacritiques, espaces, ponctuation)
}

const toNumber = (v) => {
    if (v == null) return 0;
    if (v instanceof Date) return NaN;          // une date dans une colonne montant → anomalie
    if (typeof v === 'number') return v;
    const s = String(v).replace(/[^0-9.,-]/g, '').replace(/\s/g, '').replace(',', '.');
    const n = parseFloat(s);
    return Number.isNaN(n) ? 0 : n;
};

function construireIndexEnc(rows) {
    const headers = (rows[0] || []).map(normaliserHeader);
    const idx = {};
    // Champ "total" en dernier pour éviter qu'il ne capte "PRINCIPAL/FG ..."
    headers.forEach((h, i) => {
        if (!h) return;
        for (const [champ, syns] of Object.entries(SYNONYMES_ENC)) {
            if (idx[champ] !== undefined) continue;
            if (syns.some(s => h.includes(s))) { idx[champ] = i; break; }
        }
    });
    // Fallbacks sur les colonnes stables dans tous les formats (B=1, C=2).
    if (idx.dateReception === undefined) idx.dateReception = 1;
    if (idx.dateSinistre === undefined) idx.dateSinistre = 2;
    return idx;
}

// Détecte une ligne "TOTAL" (ligne de pied de tableau) à ignorer.
function estLigneTotal(r, I) {
    const cellules = [r[0], r[I.sinManuel], r[I.payeur], r[I.assure]]
        .map(x => String(x ?? '').toUpperCase().trim());
    return cellules.some(c => c === 'TOTAL' || c.startsWith('TOTAL '));
}

function parseEncaissements(ws) {
    const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null, cellDates: true });
    const data = [];
    const I = construireIndexEnc(rows);
    console.log('[RepriseEncaissements] Indices détectés :', I);

    for (let i = 1; i < rows.length; i++) {
        const r = rows[i];
        if (!r) continue;

        const numeroManuel = clean(r[I.sinManuel]);
        const numeroCheque = clean(r[I.cheque]);

        // Ligne TOTAL / vide → on ignore (un vrai enregistrement a date + montant)
        if (estLigneTotal(r, I)) continue;
        if (!numeroManuel && !numeroCheque) continue;

        const warnings = [];

        // Montants : NaN = la cellule contient autre chose qu'un nombre (ex. une
        // date dans une colonne montant) → on signale et on neutralise à 0.
        const lireMontant = (col, label) => {
            const n = toNumber(I[col] !== undefined ? r[I[col]] : 0);
            if (Number.isNaN(n)) { warnings.push(avert('nonNumerique', { libelle: label })); return 0; }
            return n;
        };
        const totalEncaisse = lireMontant('total', avert('champs.totalEncaisse'));
        const principalEncaisse = lireMontant('principal', avert('champs.principal'));
        const fgEncaisse = lireMontant('fg', avert('champs.fg'));
        const pgEncaisse = lireMontant('pgReel', avert('champs.pgReel'));

        const montantTheorique = principalEncaisse;
        const produitFraisGestion = fgEncaisse;
        const montantCheque = totalEncaisse > 0 ? totalEncaisse : (principalEncaisse + pgEncaisse);

        if (!numeroManuel) warnings.push(avert('numeroBncbTgManquant'));
        if (I.payeur !== undefined && !r[I.payeur]) warnings.push(avert('payeurManquant'));
        if (I.dateReception === undefined || !r[I.dateReception]) warnings.push(avert('dateReceptionManquante'));
        if (montantCheque <= 0) warnings.push(avert('montantNul'));

        data.push({
            _ligne: i + 1,
            _warnings: warnings,
            _hasWarning: warnings.length > 0,
            numeroSinistreManuel: numeroManuel,
            numeroSinistreHomologue: I.sinHomol !== undefined ? clean(r[I.sinHomol]) : null,
            dateReception: dateFromRaw(r[I.dateReception]),
            dateSinistre: I.dateSinistre !== undefined ? dateFromRaw(r[I.dateSinistre]) : null,
            organismeEmetteurCode: I.payeur !== undefined ? clean(r[I.payeur]) : null,
            assureNomComplet: I.assure !== undefined ? clean(r[I.assure]) : null,
            modePaiement: (I.modePaiem !== undefined ? clean(r[I.modePaiem])?.toUpperCase() : null) || 'BANQUE',
            banqueEmettrice: I.banque !== undefined ? clean(r[I.banque]) : null,
            numeroCheque,
            lieuSurvenance: (I.lieu !== undefined ? clean(r[I.lieu])?.toUpperCase() : null) || 'E',
            montantTheorique,
            produitFraisGestion,
            pgEncaisse,
            _montantCheque: montantCheque,
        });
    }
    return data;
}

// ── Colonnes tableau preview ───────────────────────────────────────
const COLS = (t) => [
    {
        title: '', key: 's', width: 34, fixed: 'left',
        render: (_, r) => r._hasWarning
            ? <Tooltip title={r._warnings.join(' | ')}><WarningOutlined style={{ color: '#f97316' }} /></Tooltip>
            : <CheckCircleOutlined style={{ color: '#1a6b3c' }} />,
    },
    {
        title: t('commun.colonnes.numeroSinistre'), dataIndex: 'numeroSinistreManuel', key: 'num', width: 160, fixed: 'left',
        render: v => <span style={{ fontFamily: 'var(--font-main)', fontWeight: 700, color: '#1a6b3c', fontSize: 11 }}>{v}</span>
    },
    {
        title: t('encaissements.colonnes.numeroCheque'), dataIndex: 'numeroCheque', key: 'ch', width: 120,
        render: v => <span style={{ fontFamily: 'var(--font-main)', fontSize: 11 }}>{v}</span>
    },
    {
        title: t('encaissements.colonnes.dateReception'), dataIndex: 'dateReception', key: 'dr', width: 110,
        render: v => v ? new Date(v).toLocaleDateString(localeIntl()) : <Tag color="red">{t('commun.manquant')}</Tag>
    },
    {
        title: t('encaissements.colonnes.payeur'), dataIndex: 'organismeEmetteurCode', key: 'pay', width: 130,
        render: v => <Tag color="geekblue" style={{ borderRadius: 20, fontSize: 11 }}>{v}</Tag>
    },
    { title: t('commun.colonnes.assure'), dataIndex: 'assureNomComplet', key: 'ass', ellipsis: true, width: 160 },
    {
        title: t('commun.colonnes.mode'), dataIndex: 'modePaiement', key: 'mode', width: 90,
        render: v => <Tag color={v === 'VIREMENT' ? 'purple' : 'blue'} style={{ borderRadius: 20, fontSize: 11 }}>{v}</Tag>
    },
    { title: t('encaissements.colonnes.banque'), dataIndex: 'banqueEmettrice', key: 'bq', width: 100, ellipsis: true },
    {
        title: t('encaissements.colonnes.lieu'), dataIndex: 'lieuSurvenance', key: 'lieu', width: 60, align: 'center',
        render: v => <Tag color={v === 'T' ? 'green' : 'orange'} style={{ borderRadius: 20, fontSize: 11 }}>{v}</Tag>
    },
    {
        title: t('encaissements.colonnes.montantCheque'), dataIndex: '_montantCheque', key: 'mt', width: 160, align: 'right',
        render: v => <span style={{ fontWeight: 600, color: '#166534' }}>{fMt(v)}</span>
    },
    {
        title: t('commun.colonnes.principal'), dataIndex: 'montantTheorique', key: 'prin', width: 130, align: 'right',
        render: v => fMt(v)
    },
    {
        title: t('encaissements.colonnes.pgEncaisse'), dataIndex: 'pgEncaisse', key: 'pg', width: 110, align: 'right',
        render: v => fMt(v)
    },
    {
        title: t('encaissements.colonnes.fgEncaisse'), dataIndex: 'produitFraisGestion', key: 'fg', width: 110, align: 'right',
        render: v => fMt(v)
    },
];

// exporterRapport → voir rapportUtils.js

// ════════════════════════════════════════════════════════════════════
//  COMPOSANT PRINCIPAL
// ════════════════════════════════════════════════════════════════════
export default function RepriseEncaissements() {
    const { t } = useTranslation('reprise');
    const [step, setStep] = useState(0);
    const [annee, setAnnee] = useState(new Date().getFullYear());
    const [fileName, setFileName] = useState('');
    const [data, setData] = useState([]);
    const [importing, setImporting] = useState(false);
    const [progress, setProgress] = useState(0);
    const [rapport, setRapport] = useState(null);

    // ── Stats ──────────────────────────────────────────────────────
    const stats = useMemo(() => {
        const warnings = data.filter(r => r._hasWarning).length;
        const totalMt = data.reduce((s, r) => s + r._montantCheque, 0);
        const byPayeur = {};
        data.forEach(r => {
            const p = r.organismeEmetteurCode || 'N/A';
            byPayeur[p] = (byPayeur[p] || 0) + 1;
        });
        return { total: data.length, warnings, valid: data.length - warnings, totalMt, byPayeur };
    }, [data]);

    // ── Upload ─────────────────────────────────────────────────────
    const onDrop = (file) => {
        setFileName(file.name);
        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const wb = await XLSX.read(e.target.result, { type: 'array', cellDates: true });
                const sheetName = wb.SheetNames.find(n => n.trim().toUpperCase() === `ENCAISSEMENT ${annee}`)
                    || wb.SheetNames.find(n => n.trim().toUpperCase().startsWith('ENCAISSEMENT'));
                if (!sheetName) {
                    toast.error(t('encaissements.feuilleIntrouvable', { annee, feuilles: wb.SheetNames.join(', ') }));
                    return;
                }
                const parsed = parseEncaissements(wb.Sheets[sheetName], annee);
                setData(parsed);
                setStep(1);
                toast.success(t('encaissements.feuilleChargee', { feuille: sheetName, nombre: parsed.length }));
            } catch (err) {
                toast.error(t('commun.erreurLectureExcel', { message: err.message }));
            }
        };
        reader.readAsArrayBuffer(file);
        return false;
    };

    // ── Import ─────────────────────────────────────────────────────
    const lancerImport = async () => {
        setImporting(true);
        setProgress(0);
        const BATCH = 50;
        let importes = 0, doublons = 0, erreurs = 0;
        const details = [];
        const lignesOk = [];

        for (let i = 0; i < data.length; i += BATCH) {
            const batch = data.slice(i, i + BATCH);
            try {
                const res = await axiosInstance.post('/v1/reprise/encaissements', {
                    encaissements: batch.map(r => ({
                        numeroSinistreManuel: r.numeroSinistreManuel,
                        numeroSinistreHomologue: r.numeroSinistreHomologue,
                        dateReception: r.dateReception,
                        dateSinistre: r.dateSinistre,
                        organismeEmetteurCode: r.organismeEmetteurCode,
                        assureNomComplet: r.assureNomComplet,
                        modePaiement: r.modePaiement,
                        banqueEmettrice: r.banqueEmettrice,
                        numeroCheque: r.numeroCheque,
                        lieuSurvenance: r.lieuSurvenance,
                        montantTheorique: r.montantTheorique,
                        produitFraisGestion: r.produitFraisGestion,
                        pgEncaisse: r.pgEncaisse,
                    })),
                });
                const d = res.data?.data;
                importes += d?.importes || 0;
                doublons += d?.doublons || 0;
                erreurs += d?.erreurs || 0;
                if (d?.details) {
                    // Retrouver la VRAIE ligne source : un même chèque peut régler
                    // plusieurs sinistres → chercher d'abord par le n° de sinistre
                    // porté par le message (créations auto, n° corrigés), sinon
                    // se rabattre sur le n° de chèque.
                    const cleNum = (v) => String(v || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
                    d.details.forEach(det => {
                        const m = /créé\s*:\s*(.+)$/.exec(det.message || '')
                            || /sinistre\s+(\S+)\s+absent/.exec(det.message || '')
                            || /«\s*([^»]+?)\s*»/.exec(det.message || '');
                        let src = m ? batch.find(r => cleNum(r.numeroSinistreManuel) === cleNum(m[1])) : null;
                        if (!src) src = batch.find(r =>
                            r.numeroCheque === det.numero ||
                            r.numeroSinistreManuel === det.numero);
                        details.push({
                            ...det,
                            ligne: src?._ligne,
                            assure: src?.assureNomComplet,
                            dateAccident: src?.dateSinistre,
                            paysCode: src?.organismeEmetteurCode,
                            warnings: src?._warnings || [],
                        });
                    });
                }
                // Lignes OK pour le rapport
                if (d?.importes > 0) {
                    batch.forEach(r => {
                        const rej = d?.details?.find(dt =>
                            dt.numero === r.numeroCheque || dt.numero === r.numeroSinistreManuel);
                        if (!rej || ['SINISTRE_CREE', 'VICTIME_CREEE'].includes(rej.type)) {
                            lignesOk.push(r);
                        }
                    });
                }
            } catch (err) {
                erreurs += batch.length;
                details.push({ type: 'ERROR', numero: `batch ${i}`, message: err?.response?.data?.message || err.message });
            }
            setProgress(Math.min(100, Math.round(((i + BATCH) / data.length) * 100)));
        }

        setRapport({
            totalTraite: data.length,
            importes, doublons, erreurs, details,
            lignesOk, annee,
            date: new Date().toLocaleString(localeIntl()),
        });
        setImporting(false);
        setStep(3);
    };

    const reset = () => { setStep(0); setData([]); setRapport(null); setFileName(''); };

    // ── RENDU ──────────────────────────────────────────────────────
    return (
        <Layout title={t('encaissements.titreOnglet')}>
            <div style={{ maxWidth: 1400 }}>

                {/* Assistant d'import : pas de `compte`, l'ecran demarre vide et un
                    « 0 encaissement » se lirait comme une liste vide. */}
                <PageHeader
                    titre={t('encaissements.titre')}
                    icone={<CloudUploadOutlined />}
                    actions={(
                        <Select
                            value={annee}
                            onChange={(v) => { setAnnee(v); reset(); }}
                            style={{ width: 100, borderRadius: 8 }}
                            options={ANNEES.map(a => ({ value: a, label: String(a) }))}
                        />
                    )}
                />

                {/* Étapes */}
                <div className="cb-card" style={{ marginBottom: 24 }}>
                    <Steps current={step} size="small" items={[
                        { title: t('commun.etapes.fichierExcel'), icon: <FileExcelOutlined /> },
                        { title: t('commun.etapes.previsualisation') },
                        { title: t('commun.etapes.confirmation') },
                        { title: t('commun.etapes.rapport'), icon: <BarChartOutlined /> },
                    ]} />
                </div>

                {/* ── Étape 0 : Upload ── */}
                {step === 0 && (
                    <div className="cb-card" style={{ textAlign: 'center', padding: 40 }}>
                        <Title level={4} style={{ color: '#1a6b3c', marginBottom: 8 }}>
                            {t('encaissements.charger')}
                        </Title>
                        <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
                            <Trans t={t} i18nKey="encaissements.consigneFichier" values={{ annee }}
                                components={{ strong: <strong />, br: <br /> }} />
                        </Text>
                        <Dragger
                            accept=".xlsx,.xls"
                            beforeUpload={onDrop}
                            showUploadList={false}
                            style={{ maxWidth: 600, margin: '0 auto', borderColor: '#1a6b3c', borderRadius: 12 }}
                        >
                            <p><InboxOutlined style={{ fontSize: 48, color: '#1a6b3c' }} /></p>
                            <p style={{ fontSize: 15, fontWeight: 600 }}>{t('commun.glisserDeposer')}</p>
                            <p style={{ color: '#6b7280', fontSize: 13 }}>{t('commun.ouCliquer')}</p>
                        </Dragger>
                    </div>
                )}

                {/* ── Étape 1 : Preview ── */}
                {step === 1 && (
                    <>
                        {/* Stats */}
                        <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
                            {[
                                { label: t('encaissements.totalEncaissements'), value: stats.total, bg: '#e8f5ed', color: '#1a6b3c' },
                                { label: <><CheckCircleOutlined style={{ color: '#16a34a' }} /> {t('commun.valides')}</>, value: stats.valid, bg: '#dcfce7', color: '#166534' },
                                { label: <><WarningOutlined style={{ color: '#d97706' }} /> {t('commun.alertes')}</>, value: stats.warnings, bg: '#fef3c7', color: '#92400e' },
                                { label: t('commun.montantTotalAnnee', { annee }), value: fMt(stats.totalMt), bg: '#dbeafe', color: '#1e40af' },
                            ].map((s, i) => (
                                <Col xs={12} md={6} key={i}>
                                    <div style={{ background: s.bg, borderRadius: 12, padding: '14px 18px' }}>
                                        <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</div>
                                        <div style={{ fontSize: 12, color: '#374151' }}>{s.label}</div>
                                    </div>
                                </Col>
                            ))}
                        </Row>

                        {stats.warnings > 0 && (
                            <Alert type="warning" showIcon icon={<ExclamationCircleOutlined />}
                                message={t('encaissements.lignesAvecAlertes', { count: stats.warnings })}
                                description={t('encaissements.lignesImporteesQuandMeme')}
                                style={{ marginBottom: 16, borderRadius: 10 }} />
                        )}

                        {/* Répartition par payeur */}
                        <div className="cb-card" style={{ marginBottom: 16, padding: '16px 20px' }}>
                            <div style={{ fontWeight: 700, color: '#1a6b3c', marginBottom: 12, fontSize: 13 }}>
                                {t('encaissements.repartitionPayeur')}
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                {Object.entries(stats.byPayeur).sort((a, b) => b[1] - a[1]).map(([p, n]) => (
                                    <Tag key={p} color="geekblue" style={{ borderRadius: 20, fontSize: 12, padding: '3px 10px' }}>
                                        {p} : <strong>{n}</strong>
                                    </Tag>
                                ))}
                            </div>
                        </div>

                        {/* Tableau */}
                        <div className="cb-card" style={{ padding: 0 }}>
                            <div style={{ padding: '14px 20px 8px', borderBottom: '1px solid #f3f4f6' }}>
                                <span style={{ fontWeight: 700, color: '#1a6b3c', fontSize: 13 }}>
                                    {t('encaissements.titreTableau', { annee, nombre: data.length, fichier: fileName })}
                                </span>
                            </div>
                            <Table
                                className="cb-table"
                                dataSource={data.map((r, i) => ({ ...r, key: i }))}
                                columns={COLS(t)}
                                size="small"
                                scroll={{ x: 1400, y: 420 }}
                                pagination={{ pageSize: 50, showTotal: (n) => t('encaissements.nbEncaissements', { nombre: n }) }}
                                rowClassName={r => r._hasWarning ? 'ant-table-row-warning' : ''}
                            />
                        </div>

                        <div style={{ display: 'flex', gap: 12, marginTop: 20, justifyContent: 'flex-end' }}>
                            <Button onClick={reset} style={{ borderRadius: 8 }}>{t('commun.recharger')}</Button>
                            <Button type="primary" onClick={() => setStep(2)}
                                style={{ background: '#1a6b3c', borderColor: '#1a6b3c', borderRadius: 8, height: 40, paddingInline: 24 }}>
                                {t('commun.passerConfirmation')}
                            </Button>
                        </div>
                    </>
                )}

                {/* ── Étape 2 : Confirmation ── */}
                {step === 2 && (
                    <div className="cb-card" style={{ maxWidth: 640, margin: '0 auto', textAlign: 'center', padding: 40 }}>
                        <Title level={3} style={{ color: '#1a6b3c', marginBottom: 8 }}><CheckCircleOutlined style={{ color: '#16a34a' }} /> {t('commun.confirmerImport')}</Title>
                        <Text type="secondary" style={{ display: 'block', marginBottom: 28 }}>
                            <Trans t={t} i18nKey="encaissements.consigneConfirmation" values={{ nombre: stats.total }}
                                components={{ strong: <strong />, br: <br /> }} />
                        </Text>

                        <Row gutter={[16, 16]} style={{ marginBottom: 28, textAlign: 'left' }}>
                            {[
                                { label: t('encaissements.totalEncaissements'), value: stats.total, bg: '#e8f5ed', color: '#1a6b3c' },
                                { label: t('commun.montantTotalFcfa'), value: fMt(stats.totalMt), bg: '#dbeafe', color: '#1e40af' },
                                { label: t('commun.valides'), value: stats.valid, bg: '#dcfce7', color: '#166534' },
                                { label: t('commun.alertes'), value: stats.warnings, bg: '#fef3c7', color: '#92400e' },
                            ].map((s, i) => (
                                <Col span={12} key={i}>
                                    <div style={{ background: s.bg, borderRadius: 10, padding: '12px 16px' }}>
                                        <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</div>
                                        <div style={{ fontSize: 12, color: '#374151' }}>{s.label}</div>
                                    </div>
                                </Col>
                            ))}
                        </Row>

                        {importing && (
                            <div style={{ marginBottom: 24 }}>
                                <Progress percent={progress} status="active" strokeColor="#1a6b3c" />
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    {t('commun.importEnCours', { fait: Math.round(progress * stats.total / 100), total: stats.total })}
                                </Text>
                            </div>
                        )}

                        {!importing && (
                            <Space>
                                <Button onClick={() => setStep(1)} style={{ borderRadius: 8 }}>{t('commun.retour')}</Button>
                                <Button type="primary" size="large" icon={<CloudUploadOutlined />}
                                    onClick={lancerImport}
                                    style={{ background: '#1a6b3c', borderColor: '#1a6b3c', borderRadius: 8, paddingInline: 28 }}>
                                    {t('encaissements.lancerImport', { nombre: stats.total })}
                                </Button>
                            </Space>
                        )}
                    </div>
                )}

                {/* ── Étape 3 : Rapport ── */}
                {step === 3 && rapport && (
                    <>
                        <Result
                            icon={rapport.erreurs === 0
                                ? <CheckCircleOutlined style={{ color: '#1a6b3c' }} />
                                : <WarningOutlined style={{ color: '#f97316' }} />}
                            status={rapport.erreurs === 0 ? 'success' : 'warning'}
                            title={t('encaissements.importTermine', { nombre: rapport.importes })}
                            subTitle={t('encaissements.sousTitreRapport', { annee, date: rapport.date })}
                        />

                        <div className="cb-card" style={{ maxWidth: 700, margin: '0 auto 20px' }}>
                            <Title level={4} style={{ color: '#1a6b3c', marginBottom: 20 }}>
                                {t('encaissements.titreRapport', { annee })}
                            </Title>
                            <Row gutter={[16, 16]}>
                                {[
                                    { label: t('commun.totalTraite'), value: rapport.totalTraite, bg: '#f4f6f5', color: '#374151' },
                                    { label: <><CheckCircleOutlined style={{ color: '#16a34a' }} /> {t('commun.importes')}</>, value: rapport.importes, bg: '#dcfce7', color: '#166534' },
                                    { label: t('commun.doublonsIcone'), value: rapport.doublons, bg: '#fef3c7', color: '#92400e' },
                                    { label: <><CloseCircleOutlined style={{ color: '#dc2626' }} /> {t('commun.erreurs')}</>, value: rapport.erreurs, bg: '#fee2e2', color: '#991b1b' },
                                ].map((s, i) => (
                                    <Col span={6} key={i}>
                                        <div style={{
                                            background: s.bg, borderRadius: 10, padding: '14px 16px',
                                            borderLeft: `4px solid ${s.color}`
                                        }}>
                                            <div style={{ fontSize: 28, fontWeight: 800, color: s.color }}>{s.value}</div>
                                            <div style={{ fontSize: 12 }}>{s.label}</div>
                                        </div>
                                    </Col>
                                ))}
                            </Row>

                            {/* Anomalies tableau */}
                            {rapport.details?.length > 0 && (
                                <div style={{ marginTop: 16 }}>
                                    <Divider>
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            {t('commun.anomaliesLignes', { count: rapport.details.length })}
                                        </Text>
                                    </Divider>
                                    <Table size="small" scroll={{ x: 800 }}
                                        dataSource={rapport.details.slice(0, 50).map((d, i) => ({
                                            ...d, key: i, _cat: categoriserErreur(d)
                                        }))}
                                        columns={[
                                            {
                                                title: t('commun.colonnes.ligne'), dataIndex: 'ligne', width: 60,
                                                render: v => v ? <code>L{v}</code> : '-'
                                            },
                                            {
                                                title: t('commun.colonnes.type'), dataIndex: 'type', width: 120,
                                                render: v => <Tag color={TAG_COLORS[v] || 'default'} style={{ fontSize: 10 }}>{v}</Tag>
                                            },
                                            {
                                                title: t('commun.colonnes.numeroReference'), dataIndex: 'numero', width: 170,
                                                render: v => <code style={{ fontSize: 11 }}>{v || '-'}</code>
                                            },
                                            {
                                                title: t('commun.colonnes.raison'), key: 'raison', ellipsis: true,
                                                render: (_, r) => RAISONS[r._cat] || r.message
                                            },
                                            {
                                                title: t('commun.colonnes.actionCorrective'), key: 'action', width: 260, ellipsis: true,
                                                render: (_, r) => <span style={{ fontSize: 11, color: couleurAction(r._cat) }}>
                                                    {ACTIONS[r._cat] || t('commun.interventionManuelle')}
                                                </span>
                                            },
                                        ]}
                                        pagination={{ pageSize: 10, showTotal: (n) => t('commun.nbAnomalies', { count: n }) }}
                                    />
                                </div>
                            )}

                            <div style={{ display: 'flex', gap: 12, marginTop: 20, justifyContent: 'center', flexWrap: 'wrap' }}>
                                <Button icon={<DownloadOutlined />} onClick={() => exporterRapportEncaissements(rapport, annee)}
                                    style={{ borderRadius: 8 }}>
                                    {t('commun.exporterRapport')}
                                </Button>
                                <Button icon={<ReloadOutlined />} onClick={reset} style={{ borderRadius: 8 }}>
                                    {t('commun.nouvelleReprise')}
                                </Button>
                            </div>
                        </div>
                    </>
                )}
            </div>

            <style>{`
        .ant-table-row-warning td { background: #fff7ed !important; }
        .ant-table-row-warning:hover td { background: #fef3c7 !important; }
      `}</style>
        </Layout>
    );
}